/**
 * Term — 글로서리 기반 용어 래퍼.
 *
 *   <Term name="slice">슬라이스</Term>
 *     → 호버 시 GLOSSARY[slice].summary 가 툴팁으로 뜨고
 *       "자세히 →" 링크가 /docs/concepts/glossary#slice 로 연결.
 *
 * `name` 미매칭이면 툴팁을 띄우지 않고 children 을 그대로 렌더 — 문서 작성 실수에 안전.
 * docs-site + dashboard 공용. linkPrefix 로 각 호스트의 라우트 베이스를 주입한다
 * (docs-site 기본값은 /docs, dashboard 는 없음).
 */
import type { ReactNode } from "react"
import { lookupTerm } from "@/shared/glossary/terms.ts"
import styles from "./Term.module.css"
import { Tooltip } from "./Tooltip.tsx"

export interface TermProps {
	/** 글로서리 id 또는 alias (예: "slice", "슬라이스", "OntologySlice") */
	readonly name: string
	/** 화면 표시 텍스트. 생략 시 glossary.label 사용 */
	readonly children?: ReactNode
	/** glossary 페이지 URL prefix. 기본 "/docs/concepts/glossary" */
	readonly linkPrefix?: string
	/** 툴팁 비활성 (인쇄용/시각 테스트) */
	readonly disableTooltip?: boolean
}

export function Term({ name, children, linkPrefix = "/docs/concepts/glossary", disableTooltip }: TermProps) {
	const term = lookupTerm(name)
	const label = children ?? term?.label ?? name

	if (!term || disableTooltip) {
		return <span className={styles.term}>{label}</span>
	}

	return (
		<Tooltip
			maxWidth={340}
			content={
				<div className={styles.popoverBody}>
					<strong className={styles.popoverTitle}>{term.label}</strong>
					<p className={styles.popoverSummary}>{term.summary}</p>
					<a className={styles.popoverLink} href={`${linkPrefix}#${term.id}`} tabIndex={-1}>
						자세히 →
					</a>
				</div>
			}
		>
			<span className={styles.term}>{label}</span>
		</Tooltip>
	)
}
